import { setJWT, setProfileHeader, setRefreshToken } from "@/api";
import cloneDeep from "lodash/cloneDeep";
import { nextTick } from "vue";

const noNull = (str) => {
    return str || "";
};

const resetModules = ({ modules, commit }) => {
    Object.keys(modules).forEach((key) => {
        commit(`${key}/reset`, { commit }, { root: true });
    });
};

const getDefaultState = () => {
    return {
        currentProfileId: null,
        currentProfile: null,
        userData: {},
        user: {},
        profiles: [],
        loginMethod: null,
        permissions: [],
    };
};

/**
 * Rensar alla sparade inställningar i session storage för Primevue table
 */
const clearTableSettings = async () => {
    // Behövs för att försäkra oss om att Primevue table är avrenderad och inte försöker återskapa items
    await nextTick();

    const settingKeys = Object.keys(sessionStorage ?? {}).filter((key) => {
        return key.includes("-table") || key.includes("-table-custom");
    });

    settingKeys.forEach((key) => {
        sessionStorage.removeItem(key);
    });
};

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        reset(state) {
            Object.assign(state, getDefaultState());
        },
        resetCurrentProfile(state) {
            state.currentProfile = null;
            state.currentProfileId = null;
            setProfileHeader(null);
        },
        setProfiles(state, profiles) {
            state.profiles = profiles;
        },
        setUserData(state, userData) {
            state.userData = userData;
        },
        setCurrentProfileId(state, id) {
            state.currentProfileId = id;
            state.currentProfile = state.profiles.find((el) => state.currentProfileId === el.pk);
            setProfileHeader(id);
            if (process.env.NODE_ENV === "development") {
                sessionStorage.setItem("ProfileId", id);
            }
        },
        setLoginMethod(state, method) {
            state.setLoginMethod = method;
        },
        setUser(state, user) {
            state.user = user;
        },
        setPermissions(state, permissions) {
            state.permissions = permissions;
        },
        findAndSetProfile(state, profile) {
            const foundProfileIndex = state.profiles.findIndex((p) => p.pk === profile.pk);

            if (foundProfileIndex < 0) {
                return;
            }

            const profiles = [...state.profiles];

            profiles[foundProfileIndex] = profile;

            state.profiles = profiles;
        },
        setCurrentProfileTableSettings(state, tableSettings) {
            state.currentProfile = {
                ...cloneDeep(state.currentProfile),
                table_settings: tableSettings,
            };

            const foundProfileIndex = state.profiles.findIndex((p) => p.pk === state.currentProfile.pk);

            if (foundProfileIndex < 0) {
                return;
            }

            const profiles = [...state.profiles];

            profiles[foundProfileIndex] = state.currentProfile;

            state.profiles = profiles;
        },
    },
    getters: {
        personId(state) {
            return state.userData.personId || "[Saknas]";
        },
        fullName(state) {
            return state.userData.first_name || state.userData.last_name
                ? noNull(state.userData.first_name) + " " + noNull(state.userData.last_name)
                : "Användare saknas";
        },
        initials(state) {
            return state.userData.first_name || state.userData.last_name
                ? noNull(state.userData.first_name).slice(0, 1) + noNull(state.userData.last_name).slice(0, 1)
                : "--";
        },
        username(state) {
            return state.userData.username;
        },
        HSAId(state) {
            return state.userData.HSAId;
        },
        arbKod(state) {
            return state.currentProfile.orgUnit.arbetsplatskod;
        },
        currentProfileId(state) {
            return state.currentProfileId;
        },
        currentProfile(state) {
            return state.currentProfile;
        },
        /**
         * Occupation of the current profile
         */
        occupation(state) {
            return state.currentProfile.yrkeskategori ? state.currentProfile.yrkeskategori.displayName : "Annat";
        },
        is_superuser(state) {
            return state.userData.is_superuser;
        },
        is_verksamhetsadmin(state) {
            return state.userData.is_verksamhetsadmin;
        },
        enhetsadministrator(state) {
            return state.currentProfile.enhetsadministrator;
        },
        profiles(state) {
            return state.profiles;
        },
        activeProfiles(state) {
            return state.profiles.filter((el) => el.is_active);
        },
        user(state) {
            return state.userData;
        },
    },
    actions: {
        loadProfiles({ commit }, { profiles }) {
            commit("setProfiles", profiles);
            if (profiles.length > 0) commit("setUserData", profiles[0].user);
            if (profiles.length > 0) commit("setUser", profiles[0]);
        },
        changeProfile({ commit, rootState }) {
            sessionStorage.removeItem("ProfileId");
            sessionStorage.removeItem("PatientId");
            clearTableSettings();
            commit("resetCurrentProfile");

            // loginMode ska inte rensas baserat på användare
            // userData ska behållas för att inte tvinga användaren att logga in igen
            // eslint-disable-next-line no-unused-vars
            const { userData, loginMode, ...modules } = rootState;

            resetModules({ modules, commit });
        },
        logout({ commit, rootState }) {
            sessionStorage.removeItem("Refresh");
            sessionStorage.removeItem("ProfileId");
            sessionStorage.removeItem("AuthenticationMethod");
            sessionStorage.removeItem("PatientId");
            setRefreshToken(null);
            setJWT(null);
            setProfileHeader(null);
            clearTableSettings();

            // loginMode och idle ska inte rensas baserat på användare
            // eslint-disable-next-line no-unused-vars
            const { loginMode, idle, ...modules } = rootState;

            resetModules({ modules, commit });
        },
    },
};
